const sv = {
  title: "Samma händelser. Nya koordinater.",
  units: "c = {one}",
  modes: "Modelläge",
  events: "Händelser",
  radar: "Radarsynkronisering",
  twin: "Två klockor",
  compose: "Två boostar",
  beta: "β = u/c",
  beta2: "β₂",
  epsilon: "ε",
  velocity: "v/c",
  gamma: "γ",
  eta: "η = artanh β",
  simultaneity: "Samtidighet genom vald händelse",
  grid: "S′-rutnät",
  hyperbolas: "Kalibreringshyperboler",
  svgTitle: "Minkowskidiagram i ett rum och en tid",
  svgDesc:
    "Tiden t uppåt, x åt höger. Dra A, B eller C. Fokusera en händelse och använd piltangenter för att flytta den med {step} enhet, eller skift och piltangent för {shift}.",
  move: "{name}: t {t}, x {x}. Flytta med piltangenter.",
  select: "Välj händelse {name}",
  legend: "Teckenförklaring",
  light: "ljus, |dx/dt| = {one}",
  eventLegend: "händelser / resande klocka",
  coordinates: "Händelser i S och S prim",
  event: "Händelse",
  compare: "Jämför",
  simultaneous: "Gör paret samtidigt",
  possible: "Sätt β = Δt/Δx",
  outside: "Samtidighetsramen ligger utanför reglagets ±{max}.",
  impossible: "Detta par kan inte göras samtidigt genom en tillåten boost.",
  coincident: "sammanfallande",
  lightlike: "ljuslik",
  timelike: "tidslik",
  spacelike: "rumslik",
  sameTime: "samtidiga i S′",
  after: "{b} efter {a} i S′",
  before: "{b} före {a} i S′",
  check: "Kontroll i S′: {interval} · Δt′ = {time} · {order}",
  coincidentNote: "Samma händelse; ingen tidsordning.",
  spacelikeNote: "Ordningen byter vid β* = {beta}{outside}.",
  outsideNote: " (utanför reglagets intervall)",
  timelikeNote: "Tidsordningen kan aldrig vändas för |β| < {one}.",
  lightlikeNote: "Tidsordningen består; gränsen |β| = {one} är inget inertialsystem.",
  radarTable: "Fasta signalhändelser i Einsteinkoordinater",
  radarReadout:
    "Radaravstånd ρ = ({plus}−{minus})/{two} = {distance}. Klockans egentid E→R = {duration}. Tilldelad reflektionstid t′ε(P) = {time}.",
  twinTable: "Klockor mellan samma avresa och återkomst",
  coordinateDuration: "Δt i S",
  primedDuration: "Δt′ i S′",
  path: "Bana",
  home: "Hemma",
  travel: "Tur/retur",
  twinReadout:
    "Samma ändhändelser ger samma koordinattidsintervall för båda banorna i varje vald ram. Egentiden beror på banan. Skillnad: {difference}.",
  eventsCaption:
    "Enhetsmärkena anger koordinaten längs respektive axel. Streckade hyperboler: x²−t² = ±{one}. Ingen euklidisk rotation.",
  radarCaption:
    "E: sändning. P: reflektion vid x′ = {two}. R: mottagning. β flyttar hela radarapparaten relativt S; ε ändrar bara tidstilldelningen. Skalan anpassas vid stora |β|.",
  twinCaption:
    "Prickade linjer från vändningen: utresans och hemresans samtidighet. Insatsen visar τ(t) i S. Inget globalt inertialt skeppssystem; ingen gravitation.",
  composeCaption:
    "Gröna axlar: första boosten. Indigofärgade axlar: sammansättningen. β₂ är hastigheten hos S″ relativt S′, inte relativt S.",
  eventsNote:
    "Dra A, B och C eller använd piltangenter ({step}; skift: {shift}). Ändra β och följ Δt′. Den streckade gröna linjen är samtidigt med {name} i S′.",
  radarNote:
    "En klocka, två ljussträckor. τ₋ = {minus}, τ₊ = {plus}. ε = {epsilon} ger t′ε(P) = {time}. Grönt visar Einsteinmittpunkten M; rött visar Mε. {coincidence} c₊ = {outbound}, |c₋| = {inbound} i ε-koordinater. Varken ljusbanor eller klocktick ändras när ε ändras.",
  coincide: "De sammanfaller.",
  differ: "De tilldelar samma reflektion olika koordinattider.",
  twinNote:
    "Två inertiala ben, momentan vändning. T = {duration}, v/c = {velocity}. Hemmaklockan får τ = {duration}; resanden får τ = {tau}. Varje röd prick motsvarar en egentidsenhet. β-reglaget byter avläsningsram; v-reglaget ändrar själva banan.",
  composeNote:
    "S → S′ → S″. β₁ = {beta}, β₂ = {beta2}. η₁ + η₂ = {eta}; β₁₂ = {combined}. Indigofärgade axlar visar S″. {name} efter två boostar: (t″, x″) = ({t}, {x}). Direkt boost med β₁₂ ger samma koordinater.",
  ether: "S: antagen eterram",
  derived: "D = B + C − A",
  area: "Area: {area}",
  emission: "E · τ₋ = {time}",
  reception: "R · τ₊ = {time}",
  reflection: "P · reflektion",
  midpoint: "M · τ = {time}",
  epsilonMidpoint: "Mε · τ = {time}",
  turnaround: "Vändning",
  reunion: "Återkomst · t = {time}",
  outbound: "ut: {time}",
  inbound: "hem: {time}",
  inset: "τ mot t i S",
};
const en: Record<keyof typeof sv, string> = {
  title: "Same events. New coordinates.",
  units: "c = {one}",
  modes: "Model mode",
  events: "Events",
  radar: "Radar synchronisation",
  twin: "Two clocks",
  compose: "Two boosts",
  beta: "β = u/c",
  beta2: "β₂",
  epsilon: "ε",
  velocity: "v/c",
  gamma: "γ",
  eta: "η = artanh β",
  simultaneity: "Simultaneity through the selected event",
  grid: "S′ grid",
  hyperbolas: "Calibration hyperbolas",
  svgTitle: "Minkowski diagram in one space and one time dimension",
  svgDesc:
    "Time t upwards, x to the right. Drag A, B or C. Focus an event and use arrow keys to move it by {step} units, or Shift and an arrow key for {shift}.",
  move: "{name}: t {t}, x {x}. Move with arrow keys.",
  select: "Select event {name}",
  legend: "Legend",
  light: "light, |dx/dt| = {one}",
  eventLegend: "events / travelling clock",
  coordinates: "Events in S and S prime",
  event: "Event",
  compare: "Compare",
  simultaneous: "Make the pair simultaneous",
  possible: "Set β = Δt/Δx",
  outside: "The simultaneity frame is outside the slider's ±{max}.",
  impossible: "This pair cannot be made simultaneous by an allowed boost.",
  coincident: "coincident",
  lightlike: "lightlike",
  timelike: "timelike",
  spacelike: "spacelike",
  sameTime: "simultaneous in S′",
  after: "{b} after {a} in S′",
  before: "{b} before {a} in S′",
  check: "Check in S′: {interval} · Δt′ = {time} · {order}",
  coincidentNote: "The same event; no time ordering.",
  spacelikeNote: "The order changes at β* = {beta}{outside}.",
  outsideNote: " (outside the slider range)",
  timelikeNote: "The time order can never reverse for |β| < {one}.",
  lightlikeNote: "The time order persists; the limit |β| = {one} is not an inertial frame.",
  radarTable: "Fixed signal events in Einstein coordinates",
  radarReadout:
    "Radar distance ρ = ({plus}−{minus})/{two} = {distance}. Clock proper time E→R = {duration}. Assigned reflection time t′ε(P) = {time}.",
  twinTable: "Clocks between the same departure and reunion",
  coordinateDuration: "Δt in S",
  primedDuration: "Δt′ in S′",
  path: "Path",
  home: "Home",
  travel: "Round trip",
  twinReadout:
    "The same endpoints give the same coordinate time interval for both paths in each selected frame. Proper time depends on the path. Difference: {difference}.",
  eventsCaption:
    "Unit marks give the coordinate along each axis. Dashed hyperbolas: x²−t² = ±{one}. No Euclidean rotation.",
  radarCaption:
    "E: emission. P: reflection at x′ = {two}. R: reception. β moves the whole radar apparatus relative to S; ε only changes the time assignment. The scale adapts at large |β|.",
  twinCaption:
    "Dotted lines from the turnaround: outbound and inbound simultaneity. The inset shows τ(t) in S. No global inertial ship frame; no gravity.",
  composeCaption:
    "Green axes: first boost. Indigo axes: composition. β₂ is the velocity of S″ relative to S′, not relative to S.",
  eventsNote:
    "Drag A, B and C or use arrow keys ({step}; Shift: {shift}). Change β and follow Δt′. The dashed green line is simultaneous with {name} in S′.",
  radarNote:
    "One clock, two light paths. τ₋ = {minus}, τ₊ = {plus}. ε = {epsilon} gives t′ε(P) = {time}. Green shows the Einstein midpoint M; red shows Mε. {coincidence} c₊ = {outbound}, |c₋| = {inbound} in ε coordinates. Neither light paths nor clock ticks change when ε changes.",
  coincide: "They coincide.",
  differ: "They assign different coordinate times to the same reflection.",
  twinNote:
    "Two inertial legs, instantaneous turnaround. T = {duration}, v/c = {velocity}. The home clock records τ = {duration}; the traveller records τ = {tau}. Each red dot represents one unit of proper time. The β slider changes the reading frame; the v slider changes the path itself.",
  composeNote:
    "S → S′ → S″. β₁ = {beta}, β₂ = {beta2}. η₁ + η₂ = {eta}; β₁₂ = {combined}. Indigo axes show S″. {name} after two boosts: (t″, x″) = ({t}, {x}). A direct boost with β₁₂ gives the same coordinates.",
  ether: "S: assumed ether frame",
  derived: "D = B + C − A",
  area: "Area: {area}",
  emission: "E · τ₋ = {time}",
  reception: "R · τ₊ = {time}",
  reflection: "P · reflection",
  midpoint: "M · τ = {time}",
  epsilonMidpoint: "Mε · τ = {time}",
  turnaround: "Turnaround",
  reunion: "Reunion · t = {time}",
  outbound: "out: {time}",
  inbound: "home: {time}",
  inset: "τ against t in S",
};
const namespace = (strings: Record<string, string>) =>
  Object.fromEntries(
    Object.entries(strings).map(([key, value]) => [`teacher.prep.spacetime.${key}`, value]),
  );
export const dict = { sv: namespace(sv), en: namespace(en) };
export type TextKey = keyof typeof sv;
